import * as d3 from 'd3';
import { sankey, SankeyLayout, SankeyGraph, sankeyLeft, SankeyNode, SankeyLink, sankeyLinkHorizontal } from 'd3-sankey';
import { formatPercent } from '@/helpers/jsUtils';
import { Config } from 'vega';
import { exportSvgAsPng, ISharedD3ChartProps } from '../../vis-shared';

import { Size, Margin } from '../../visTypes';

export interface ISankeyNode {
  id: string;
  label: string;
  pct?: number;
}

export interface ISankeyLink {
  source: string;
  target: string;
  value: number;
}

export interface ISankeyConfig extends ISharedD3ChartProps {
  target: any;
  nodes: ISankeyNode[];
  links: ISankeyLink[];
  height?: number;
  width?: number;
  showValues?: boolean;
}

const yellowGrad = { color: 'yellow', values: ['#FCDF94', '#FCDF9499', '#FCDF94'] };
const blueGrad = { color: 'blue', values: ['#D8DFF6', '#D8DFF699', '#D8DFF6'] };
const redGrad = { color: 'red', values: ['#FFD7D1', '#FFD7D199', '#FFD7D1'] };

const css = `
text {
  font-family: 'Manrope', 'Inter var', 'Inter', sans-serif;
}
`;
export class Sankey {
  target: any;
  size: Size;
  chartSize: Size;
  margin: Margin;
  svg: d3.Selection<any, any, any, any>;
  chartSelection: d3.Selection<SVGGElement, any, any, any>;
  tooltip: d3.Selection<HTMLDivElement, any, any, any>;
  sankeyGenerator: SankeyLayout<SankeyGraph<ISankeyNode, ISankeyLink>, ISankeyNode, ISankeyLink>;
  nodes: SankeyNode<ISankeyNode, ISankeyLink>[];
  links: SankeyLink<ISankeyNode, ISankeyLink>[];
  exportFileName: string;
  exportTitle: string | null;
  animated: boolean;

  constructor(config: ISankeyConfig) {
    this.target = config.target;
    this.exportFileName = config.exportFileName || 'SankeyDiagram';
    this.exportTitle = config.exportTitle || null;
    this.animated = true;
    const domWidth = Math.floor(parseInt(d3.select(this.target).style('width'), 10));
    const width = config.width || domWidth;
    const height = config.height || 20;
    this.size = {
      width,
      height,
    };
    // const rightMargin = 0; // this.calculateNeededMargin(config.nodes.map((d) => d.label));
    this.margin = {
      top: 5,
      bottom: 5,
      left: 0,
      right: 0,
    };
    this.chartSize = {
      width: width - this.margin.left - this.margin.right,
      height: height - this.margin.top - this.margin.bottom,
    };

    this.svg = d3
      .select(this.target)
      .style('position', 'relative')
      .append('svg')
      .classed('sankey', true)
      .attr('height', this.size.height)
      .attr('width', this.size.width);

    this.svg.append('style').text(css);

    this.chartSelection = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    this.tooltip = d3
      .select('body')
      .append('div')
      .style('visibility', 'hidden')
      .style('width', '110px')
      .style('font-size', '0.8em')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('z-index', 150);

    this.sankeyGenerator = sankey<ISankeyNode, ISankeyLink>()
      .nodeId((d: any) => d.id)
      .nodeAlign(sankeyLeft)
      .nodeWidth(14)
      .nodePadding(16)
      // @ts-ignore
      .nodeSort(null)
      .extent([
        [0, 0],
        [this.chartSize.width, this.chartSize.height],
      ]);

    const { nodes, links } = this.sankeyGenerator({ links: [...config.links], nodes: [...config.nodes] });
    this.nodes = nodes;
    this.links = links;
    this.draw();
  }

  draw(): void {
    // @ts-ignore
    this.chartSelection
      .append('g')
      .selectAll('rect')
      .data(this.nodes)
      .join('rect')
      // @ts-ignore
      .attr('x', (d) => d.x0 + 3)
      .attr('y', (d) => d.y0)
      // @ts-ignore
      .attr('height', (d) => d.y1 - d.y0)
      // @ts-ignore
      .attr('width', (d) => d.x1 - d.x0 - 6)
      .attr('rx', 4)
      .attr('ry', 2)
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .attr('fill', (d) => {
        if (['initial'].includes(d.id)) {
          return '#3C5CCF';
        }
        if (['entered'].includes(d.id)) {
          return '#23388300';
        }
        if (['canceled'].includes(d.id)) {
          return '#FF715B';
        }
        return '#F7B200';
      });

    const link = this.chartSelection
      .append('g')
      .selectAll('g.link-group')
      .data(this.links)
      .join('g')
      .classed('link-group', true)
      .style('mix-blend-mode', 'multiply');

    if (true) {
      const gradient = link
        .append('linearGradient')
        // @ts-ignore
        // eslint-disable-next-line no-return-assign
        .attr('id', (d) => `grad-${d.source.id}-${d.target.id}`)
        .attr('gradientUnits', 'userSpaceOnUse')
        // @ts-ignore
        // .attr('x1', (d) => d.source.x1)
        .attr('x1', '0%')
        // @ts-ignore
        // .attr('x2', (d) => d.target.x0)
        .attr('x2', '100%')
        .attr('spreadMethod', 'reflect');

      // @ts-ignore
      gradient
        .selectAll('.stop')
        // @ts.ignore
        .data((d) => {
          // @ts-ignore
          if (d.target.id === 'canceled') {
            return redGrad.values;
          }
          // @ts-ignore
          if (d.target.id === 'entered') {
            return blueGrad.values;
          }
          return yellowGrad.values;
        })
        .enter()
        .append('stop')
        .attr('offset', (d, i) => i / (yellowGrad.values.length - 1))
        .attr('stop-color', (d) => d);

      if (this.animated) {
        gradient
          .append('animate')
          .attr('attributeName', 'x1')
          .attr('values', '0%;100%')
          .attr('dur', '6s')
          .attr('repeatCount', 1);

        gradient
          .append('animate')
          .attr('attributeName', 'x2')
          .attr('values', '100%;200%')
          .attr('dur', '6s')
          .attr('repeatCount', 1);
      } else {
        gradient
          .append('animate')
          .attr('attributeName', 'x1')
          .attr('values', '50%;50%%')
          .attr('dur', '0.1s')
          .attr('repeatCount', 1);

        gradient
          .append('animate')
          .attr('attributeName', 'x2')
          .attr('values', '150%;150%')
          .attr('dur', '0.1s')
          .attr('repeatCount', 1);
      }

      // gradient
      //   .append('stop')
      //   .attr('offset', '0%')
      //   .attr('stop-color', (d) => color(d.source));

      // gradient
      //   .append('stop')
      //   .attr('offset', '100%')
      //   .attr('stop-color', (d) => color(d.target));
    }

    link
      .append('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('fill', 'none')
      .attr('stroke-width', (d) => Math.max(1, d.width || 1))
      // @ts-ignore
      .style('stroke', (d) => `url(#grad-${d.source.id}-${d.target.id})`);
    // .style('stroke', (d) => {
    //   // @ts-ignore
    //   if (d.target.id === 'canceled') {
    //     return '#FFD7D1';
    //   }
    //   // @ts-ignore
    //   if (d.target.id === 'entered') {
    //     return '#D8DFF6';
    //   }
    //   return '#FCDF94';
    // });

    this.chartSelection
      .append('g')
      .selectAll('text')
      .data(this.nodes.filter((d) => d.id !== 'entered'))
      .join('text')
      .attr('font-family', 'Inter var')
      .style('font-weight', 700)
      .style('font-size', '12px')
      .style('text-transform', 'uppercase')
      // @ts-ignore
      .attr('x', (d) => (d.x0 < this.chartSize.width / 4 ? d.x1 + 6 : d.x0 - 6))
      // @ts-ignore
      .attr('y', (d) => (d.y1 + d.y0) / 2)
      // @ts-ignore
      .attr('text-anchor', (d) => (d.x0 > this.chartSize.width / 4 ? 'end' : 'start'))
      .attr('fill', (d) => {
        if (['canceled'].includes(d.id)) {
          return '#FF715B';
        }
        if (['entered'].includes(d.id)) {
          return '#3C5CCF';
        }
        if (['initial'].includes(d.id)) {
          return '#3C5CCF';
        }
        return '#635124';
      })
      .attr('dy', '0.35em')
      // @ts-ignore
      .text((d) => `${d.label}: ${d.value}${d.pct ? ` (${formatPercent(d.pct)})` : ''}`);
  }

  calculateNeededMargin(labels: string[]): number {
    const svg = d3.select(this.target).append('svg');
    // Calculate the width needed for the labels on the left side
    const widthCalcContainer = svg
      .append('text')
      .attr('font-family', 'Inter var')
      .style('font-weight', 700)
      .style('font-size', '12px')
      .style('text-transform', 'uppercase');
    const widthCalc = widthCalcContainer.append('tspan');
    let maxTextLength = 0;
    labels.forEach((d) => {
      widthCalc.text(d);
      // @ts-ignore
      const w = widthCalc.node().getComputedTextLength();
      maxTextLength = Math.max(maxTextLength, w);
    });
    svg.remove();
    return maxTextLength;
  }

  //  updateData(nodes: ISankeyNode[], links: ISankeyLink[]): void {
  //    this.clear();
  //    this.series = series;
  //    this.draw();
  //  }

  //  clear(): void {
  //    d3.select(this.target).html('');
  //  }

  exportToPng(): void {
    const customCss = 'text { font-family: Manrope }';
    exportSvgAsPng(this.svg.node(), {
      customCss,
      scale: 3,
      backgroundColor: 'white',
      fileName: this.exportFileName,
      title: this.exportTitle || undefined,
    });
  }
}
